import { Fragment, ReactNode } from "react";
import t from "@/lang/lang";
import { Card, CardContent } from "@/shared/components/ui/card";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { Contract } from "../types/contract";
import { ScopeOfWork } from "../../scope-of-work/types/scope-of-work";
import { ContractScopeOfWorkAddDialogConfig } from "./contract-scope-of-work-add-dialog";
import { ContractScopeOfWorkToolbar } from "./contract-scope-of-work-toolbar";
import { useSelectedScopeOfWorkFromSearchParams } from "../../scope-of-work/hooks/use-selected-scope-of-work-from-search-params";
import { ContractScopeOfWorkCard } from "./contract-scope-of-work-card";
import { ContractScopeOfWorkPositionsTable } from "./contract-scope-of-work-positions-table";

/**
 * ContractScopeOfWorksPage manages the scope of works of the contract:
 * select, add and delete one, and edit the data and positions of the
 * selected one. The selection is kept in the URL, falling back to the
 * first. A contract may have no scope of work while it is drafted.
 */
export function ContractScopeOfWorksPage({
  contract,
  addDialogConfig,
  positions = (c, scopeOfWork) => (
    <ContractScopeOfWorkPositionsTable contract={c} scopeOfWork={scopeOfWork} />
  ),
}: {
  contract: Contract;
  addDialogConfig: ContractScopeOfWorkAddDialogConfig;
  positions?: (contract: Contract, scopeOfWork: ScopeOfWork) => ReactNode;
}) {
  const disabled = isImmutable(contract);
  const { selectedId, select, selected } =
    useSelectedScopeOfWorkFromSearchParams(contract.scopeOfWork);

  return (
    <div className="grid grid-cols-1 gap-2">
      <ContractScopeOfWorkToolbar
        contract={contract}
        addDialogConfig={addDialogConfig}
        selectedId={selectedId}
        onSelect={select}
        disabled={disabled}
      />
      {selected ? (
        <>
          <Fragment key={`positions-${selected.id}`}>
            {positions(contract, selected)}
          </Fragment>
          <ContractScopeOfWorkCard
            key={`card-${selected.id}`}
            contract={contract}
            scopeOfWork={selected}
            disabled={disabled}
          />
        </>
      ) : (
        <Card>
          <CardContent className="text-muted-foreground py-6 text-center text-sm">
            {t("Noch keine Leistungen. Fügen Sie eine Leistung hinzu.")}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
